import {
  Button,
  CoursesIcon,
  DotsVerticalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  Row,
  Select,
  SelectOptionType,
  Table,
  useDefaultSorting,
} from '@bp/ui-components';
import { FragmentType } from 'client/gql';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { BpPermissionChecker } from 'utils/PermissionChecker';
import { useIsDesktop } from '../../../hooks/useIsDesktop';
import { UseCoursesTableFragment, useCoursesTable } from '../hooks/useCoursesTable';
import { CourseTableType } from '../types/types';

type InstitutionCoursesTableProps = {
  query: FragmentType<typeof UseCoursesTableFragment> | undefined;
  permissions: BpPermissionChecker | null;
  loading: boolean;
  yearUuid: string;
  onYearSelect: (yearUuid: string) => void;
  onNavigate: (uuid: string) => void;
  onAdd: () => void;
  onEdit: (uuid: string) => void;
  onSettings: (uuid: string) => void;
  onDelete: (uuid: string, name: string) => void;
};

export const InstitutionCoursesTable = ({
  query,
  permissions,
  loading,
  yearUuid,
  onYearSelect,
  onNavigate,
  onAdd,
  onEdit,
  onSettings,
  onDelete,
}: InstitutionCoursesTableProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const { schoolyears } = useContext(OrganizationConfigContext);
  const isDesktop = useIsDesktop();

  const { data, columns } = useCoursesTable({
    query,
    columns: ['name', 'admins', 'schoolYear', 'origin', 'nextcloud', 'threema', 'community', 'visibility'],
    onNavigate,
  });

  const { sorting, onSortingChange } = useDefaultSorting();

  const schoolYearOptions: SelectOptionType[] =
    schoolyears?.map(({ name, uuid }) => {
      return {
        label: name,
        value: uuid,
      };
    }) ?? [];

  const canAdd = permissions?.canCreateCourse();

  const canEdit = (row: Row<CourseTableType>) =>
    permissions?.canUpdateCourse({
      uuid: row.original.uuid,
      managedBy: row.original.managedBy,
      organization: { uuid: organizationUuid },
    }) && row.original.organizationUuid === organizationUuid;

  const canEditSettings = (row: Row<CourseTableType>) =>
    permissions?.canUpdateGroupSettings({
      uuid: row.original.uuid,
      organization: { uuid: row.original.organizationUuid },
    });

  const canDelete = (row: Row<CourseTableType>) =>
    permissions?.canDeleteCourse({
      uuid: row.original.uuid,
      managedBy: row.original.managedBy,
      organization: { uuid: row.original.organizationUuid },
    });

  function getDropdownMenuItems(row: Row<CourseTableType>): DropdownMenuItem[] {
    const items: DropdownMenuItem[] = [];

    if (canEdit(row)) {
      items.push({
        label: t('common.edit'),
        onClick: () => onEdit(row.original.uuid),
      });
    }

    if (canEditSettings(row)) {
      items.push({
        label: t('settings.title'),
        onClick: () => onSettings(row.original.uuid),
      });
    }

    if (canDelete(row)) {
      items.push(
        { type: 'ruler' },
        {
          label: t('delete.delete'),
          color: 'error',
          onClick: () => onDelete(row.original.uuid, row.original.name),
        },
      );
    }

    return items;
  }

  return (
    <Table<CourseTableType>
      loading={loading}
      isDesktop={isDesktop}
      showSort
      sorting={sorting}
      onSortingChange={onSortingChange}
      data-cy='courseList'
      canScroll
      isOnWhite={false}
      showBorderRadius
      showShadow
      showActionBar
      actionBarSettings={{
        showAddButton: canAdd,
        addButtonText: t('courses.addCourse'),
        showExpertFilter: true,
        extendedActionsRight: (
          <div style={{ width: '136px' }}>
            <Select
              value={schoolYearOptions.find((y) => y.value === yearUuid)}
              options={schoolYearOptions}
              onChange={(option) => {
                onYearSelect((option as SingleValue<SelectOptionType>)?.value as string);
              }}
              name='school-year-select'
              dense
            />
          </div>
        ),
      }}
      onAddClick={onAdd}
      emptyStateSettings={{
        icon: <CoursesIcon />,
        padding: 'xl',
      }}
      columns={columns}
      data={data}
      lastColWidth='32px'
      lastCol={(row) => {
        const items = getDropdownMenuItems(row);
        const disabled = items.length === 0;
        return (
          <Dropdown
            disabled={disabled}
            trigger={<Button disabled={disabled} hierarchy='ghost' icon={<DotsVerticalIcon />} />}
          >
            <DropdownMenu data={items} />
          </Dropdown>
        );
      }}
    />
  );
};
